import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Avatar,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { authAPI, authUtils } from '../services/api';

const TenantPage = () => {
  const { t, i18n } = useTranslation();
  const [tenantInfo, setTenantInfo] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // État pour le dialogue d'invitation
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'member'
  });
  const [inviteLoading, setInviteLoading] = useState(false);

  // État pour le dialogue de suppression d'utilisateur
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // État pour la modification du tenant
  const [isEditingTenant, setIsEditingTenant] = useState(false);
  const [editTenantData, setEditTenantData] = useState({
    nom: '',
    description: ''
  });
  const [editTenantLoading, setEditTenantLoading] = useState(false);

  const currentUser = authUtils.getCurrentUser();

  useEffect(() => {
    loadTenantData();
  }, []);

  const loadTenantData = async () => {
    try {
      setLoading(true);
      const [tenantResponse, usersResponse] = await Promise.all([
        authAPI.getTenantInfo(),
        authAPI.getTenantUsers()
      ]);

      if (tenantResponse.data.success) {
        setTenantInfo(tenantResponse.data.data);
      }

      if (usersResponse.data.success) {
        setUsers(usersResponse.data.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setError(t('tenantPage.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteData.email.trim()) {
      setError(t('tenantPage.emailRequired'));
      return;
    }

    setInviteLoading(true);
    setError('');

    try {
      const response = await authAPI.createInvitation(inviteData.email, inviteData.role);
      
      if (response.data.success) {
        setSuccess(t('tenantPage.inviteSuccess', { email: inviteData.email }));
        setInviteDialogOpen(false);
        setInviteData({ email: '', role: 'member' });
        // Recharger les données pour voir les changements
        loadTenantData();
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'invitation:', error);
      setError(
        error.response?.data?.message || 
        t('tenantPage.inviteError')
      );
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeleteLoading(true);
    setError('');

    try {
      await authAPI.removeTenantUser(userToDelete.id);
      setSuccess(t('tenantPage.deleteSuccess', { name: userToDelete.nom, firstName: userToDelete.prenom }));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      // Recharger les données
      loadTenantData();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setError(
        error.response?.data?.message || 
        t('tenantPage.deleteError')
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleEditTenant = () => {
    setEditTenantData({
      nom: tenantInfo.nom || '',
      description: tenantInfo.description || ''
    });
    setIsEditingTenant(true);
  };

  const handleCancelEditTenant = () => {
    setIsEditingTenant(false);
    setEditTenantData({
      nom: '',
      description: ''
    });
  };

  const handleSaveTenant = async () => {
    setEditTenantLoading(true);
    setError('');

    try {
      const response = await authAPI.updateTenant(editTenantData);
      
      if (response.data.success) {
        setSuccess(t('tenantPage.updateSuccess'));
        setIsEditingTenant(false);
        await loadTenantData();
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      setError(error.response?.data?.message || t('tenantPage.updateError'));
    } finally {
      setEditTenantLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('tenantPage.never');
    const locale = i18n.language === 'en' ? 'en-US' : 'fr-FR';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        {t('tenantPage.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Informations du tenant */}
      {tenantInfo && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {t('tenantPage.organizationInfo')}
            </Typography>
            {currentUser?.role === 'admin' && !isEditingTenant && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleEditTenant}
                sx={{ borderRadius: 2 }}
              >
                {t('tenantPage.edit')}
              </Button>
            )}
          </Box>

          {isEditingTenant ? (
            // Mode édition
            <Box>
              <TextField
                fullWidth
                label={t('tenantPage.organizationName')}
                value={editTenantData.nom}
                onChange={(e) => setEditTenantData({ ...editTenantData, nom: e.target.value })}
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              <TextField
                fullWidth
                label={t('tenantPage.description')}
                value={editTenantData.description}
                onChange={(e) => setEditTenantData({ ...editTenantData, description: e.target.value })}
                margin="normal"
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveTenant}
                  disabled={editTenantLoading || !editTenantData.nom.trim()}
                  sx={{ borderRadius: 2 }}
                >
                  {editTenantLoading ? t('tenantPage.saving') : t('tenantPage.save')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCancelEditTenant}
                  disabled={editTenantLoading}
                  sx={{ borderRadius: 2 }}
                >
                  {t('tenantPage.cancel')}
                </Button>
              </Box>
            </Box>
          ) : (
            // Mode affichage
            <Box>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('tenantPage.organizationName')}
                  </Typography>
                  <Typography variant="h6">{tenantInfo.nom}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('tenantPage.memberCount')}
                  </Typography>
                  <Typography variant="h6">{tenantInfo.nombre_utilisateurs}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('tenantPage.creationDate')}
                  </Typography>
                  <Typography variant="body1">
                    {new Date(tenantInfo.date_creation).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'fr-FR')}
                  </Typography>
                </Box>
              </Box>
              {tenantInfo.description && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('tenantPage.description')}
                  </Typography>
                  <Typography variant="body1">{tenantInfo.description}</Typography>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Liste des utilisateurs */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('tenantPage.members')}
          </Typography>
          {currentUser?.role === 'admin' && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteDialogOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              {t('tenantPage.inviteMember')}
            </Button>
          )}
        </Box>

        <List>
          {users.map((user) => (
            <ListItem key={user.id} divider>
              <Avatar sx={{ mr: 2, bgcolor: user.role === 'admin' ? 'primary.main' : 'secondary.main' }}>
                {user.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
              </Avatar>
              <ListItemText
                primary={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {user.nom} {user.prenom}
                    <Chip
                      label={user.role === 'admin' ? t('tenantPage.role.admin') : t('tenantPage.role.member')}
                      size="small"
                      color={user.role === 'admin' ? 'primary' : 'default'}
                    />
                  </Box>
                }
                secondary={
                  <Box component="span" sx={{ display: 'block' }}>
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                      {user.email}
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('tenantPage.lastConnection')} {formatDate(user.derniere_connexion)}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                {currentUser?.role === 'admin' && user.id !== currentUser.id && (
                  <IconButton
                    edge="end"
                    aria-label={t('tenantPage.deleteAriaLabel')}
                    onClick={() => openDeleteDialog(user)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Dialogue d'invitation */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tenantPage.inviteDialog.title')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('tenantPage.inviteDialog.email')}
            fullWidth
            variant="outlined"
            value={inviteData.email}
            onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>{t('tenantPage.inviteDialog.role')}</InputLabel>
            <Select
              value={inviteData.role}
              label={t('tenantPage.inviteDialog.role')}
              onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: 2,
                }
              }}
            >
              <MenuItem value="member">{t('tenantPage.role.member')}</MenuItem>
              <MenuItem value="admin">{t('tenantPage.role.admin')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)} disabled={inviteLoading} sx={{ borderRadius: 2 }}>
            {t('tenantPage.inviteDialog.cancel')}
          </Button>
          <Button 
            onClick={handleInviteUser} 
            variant="contained" 
            disabled={inviteLoading}
            startIcon={inviteLoading ? <CircularProgress size={20} /> : <PersonAddIcon />}
            sx={{ borderRadius: 2 }}
          >
            {inviteLoading ? t('tenantPage.inviteDialog.sending') : t('tenantPage.inviteDialog.send')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de suppression */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('tenantPage.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            <span dangerouslySetInnerHTML={{ __html: t('tenantPage.deleteDialog.confirm', { name: userToDelete?.nom, firstName: userToDelete?.prenom }) }} />
            <br />
            {t('tenantPage.deleteDialog.irreversible')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading} sx={{ borderRadius: 2 }}>
            {t('tenantPage.deleteDialog.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
            sx={{ borderRadius: 2 }}
          >
            {deleteLoading ? t('tenantPage.deleteDialog.deleting') : t('tenantPage.deleteDialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TenantPage;
